import { useState, useEffect, useCallback } from 'react';
import { getSensors, Sensor } from '../services/sensors';
import { useWebSocket } from './useWebSocket';
import { useSession } from '../context/SessionContext';
import { DEMO_SENSORS } from '../data/demo';

/**
 * IMPORTANTE — por qué esto NO cae a DEMO_SENSORS cuando no hay datos:
 *
 * Hasta 2026-08-19 este hook hacía `if (usable.length === 0) setSensors(DEMO_SENSORS)`
 * para CUALQUIER tenant, y además caía a demo en el `catch` de cualquier error
 * de red. Resultado: un cliente real sin lecturas todavía — el caso exacto de
 * Leyer con su device Xiaoma recién dado de alta y aún sin reportar — veía los
 * sensores inventados de Georgia Pecan Farms presentados como propios.
 *
 * Ahora la decisión es explícita y viene del servidor (`isDemoTenant` en el
 * payload `connected` del WS): solo el tenant Demo ve datos demo. Cualquier
 * otro ve una lista vacía honesta, y los errores se reportan como errores.
 *
 * `sessionReady` evita el parpadeo inverso: mientras no llegue el payload del
 * WS no sabemos si es Demo, así que no se decide nada todavía.
 */
export function useSensors() {
  const [sensors, setSensors] = useState<Sensor[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDemo, setIsDemo] = useState(false);
  const [error, setError] = useState(false);
  const { ready: sessionReady, isDemoTenant } = useSession();

  const handleWebSocketMessage = useCallback((type: string, data: any) => {
    if (type === 'telemetry') {
      setSensors((prev) =>
        prev.map((s) =>
          s.imei === data.imei ? { ...s, lastReading: data.reading } : s
        )
      );
    }
  }, []);

  const { subscribe } = useWebSocket(handleWebSocketMessage);

  const loadSensors = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);
      const data = await getSensors();
      // Solo se exigen coordenadas, NO que ya haya llegado una lectura.
      // Antes se pedía `&& s.lastReading` y eso escondía por completo un
      // device recién dado de alta: existía en la base con su ubicación pero
      // no aparecía en ningún lado, y el mapa se quedaba sin marcadores. Es
      // más útil verlo en el mapa como "registrado, esperando datos"
      // (status 'offline', marcador gris) que no verlo — así se confirma de
      // un vistazo que quedó bien registrado y dónde está.
      const usable = data.filter(s => s.lat != null && s.lng != null);
      if (usable.length > 0) {
        setSensors(usable);
        setIsDemo(false);
        subscribe(usable.map(s => s.imei));
      } else if (isDemoTenant) {
        setSensors(DEMO_SENSORS);
        setIsDemo(true);
      } else {
        setSensors([]);
        setIsDemo(false);
      }
    } catch {
      // Nunca caer a demo por un error: un fallo de red no es una decisión de
      // producto, y disfrazarlo de datos hace imposible notar que la API murió.
      setSensors([]);
      setIsDemo(false);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [isDemoTenant, subscribe]);

  useEffect(() => {
    if (!sessionReady) return;
    loadSensors();
  }, [sessionReady, loadSensors]);

  return { sensors, loading: loading || !sessionReady, isDemo, error, refresh: loadSensors };
}
